import React, { useState } from "react";
import { LayoutDashboard, Calculator, Wrench, BarChart3, Bell, Building2, Users, DollarSign, ChevronDown, ChevronRight } from "lucide-react";
import { GroupDashboard, StoreView } from "./GroupDashboard";
import DESScorecard from "./DESScorecard";
import WhatIfCalculator from "./WhatIfCalculator";
import RemediationToolkit from "./RemediationToolkit";
import * as Dealer from "./DealerPages";
import * as Admin from "./AdminPages";

const PASSWORD = "MaxQDemo2026";
const dealerUsers = ["demo@maxqdemo.com","gm@riverside-ford.com"];
const adminUsers = ["jeff@maxqautomotive.com","nick@maxqautomotive.com","demo@maxqdemo.com","jhatfield@bridge-pointgrowth.com"];

const dealerNavGroups = [
  {
    group: null,
    items: [
      ["group","Group Dashboard",LayoutDashboard],
      ["overview","Store Overview",LayoutDashboard],
      ["scorecard","D.E.S. Scorecard",BarChart3],
    ]
  },
  {
    group: "Sales",
    items: [
      ["sales-dashboard","Sales Dashboard",BarChart3],
      ["pipeline","Pipeline",BarChart3],
      ["leads","All Leads",Users],
      ["marketplace","Marketplace",DollarSign],
      ["staff","Staff Performance",Users],
      ["mastermind","Equity Exchange",Calculator],
    ]
  },
  {
    group: "Service & Parts",
    items: [
      ["service-dashboard","Service Dashboard",Wrench],
      ["ro","Repair Orders",Wrench],
      ["tech","Technicians",Users],
      ["appts","Appointments",Wrench],
      ["csi","CSI & Retention",BarChart3],
      ["parts","Parts",Wrench],
    ]
  },
  {
    group: "Financial",
    items: [
      ["financials","Financial Overview",DollarSign],
      ["reports","Reports",BarChart3],
    ]
  },
  {
    group: "Tools",
    items: [
      ["remediation","Remediation Toolkit",Wrench],
      ["whatif","What-If Calculator",Calculator],
    ]
  },
];

const adminNav = [
  ["admin-overview","Admin Dashboard",LayoutDashboard],
  ["alerts","System Alerts",Bell],
  ["dealers","All Dealerships",Building2],
  ["onboard","Onboard Dealer",Building2],
  ["users","User Management",Users],
  ["connections","API Connections",BarChart3],
  ["scorecard","D.E.S. Scorecard",BarChart3],
  ["activity","Activity Log",BarChart3],
  ["billing","Billing",DollarSign],
  ["settings","Platform Settings",BarChart3],
  ["remediation","Remediation Toolkit",Wrench],
  ["whatif","What-If Calculator",Calculator],
];

function GroupedNav({ groups, page, setPage }) {
  const [collapsed, setCollapsed] = useState({});
  return (
    <div style={{display:"flex",flexDirection:"column",gap:4}}>
      {groups.map((g, gi) => (
        <div key={gi}>
          {g.group && (
            <button
              onClick={() => setCollapsed(prev => ({...prev, [g.group]: !prev[g.group]}))}
              style={{display:"flex",alignItems:"center",justifyContent:"space-between",width:"100%",background:"transparent",border:"none",color:"var(--text3)",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".6px",padding:"8px 10px 4px",cursor:"pointer"}}
            >
              {g.group}
              {collapsed[g.group] ? <ChevronRight size={10}/> : <ChevronDown size={10}/>}
            </button>
          )}
          {!collapsed[g.group] && g.items.map(([id, label, Icon]) => (
            <button
              key={id}
              className={`nav-btn ${page===id?"active":""}`}
              onClick={() => setPage(id)}
              style={{paddingLeft: g.group ? 20 : 10}}
            >
              <Icon size={13}/><span style={{fontSize:12}}>{label}</span>
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Shell() {
  const [email, setEmail] = useState("demo@maxqdemo.com");
  const [password, setPassword] = useState(PASSWORD);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [portal, setPortal] = useState("dealer");
  const [page, setPage] = useState("group");
  const [selectedStore, setSelectedStore] = useState(null);

  function handleLogin() {
    const normalized = email.trim().toLowerCase();
    const isDealer = dealerUsers.includes(normalized);
    const isAdmin = adminUsers.includes(normalized);
    if (password !== PASSWORD || (!isDealer && !isAdmin)) { setError("Invalid credentials"); return; }
    const nextPortal = isAdmin && !isDealer ? "admin" : "dealer";
    setUser({ email: normalized });
    setPortal(nextPortal);
    setPage(nextPortal === "admin" ? "admin-overview" : normalized === "demo@maxqdemo.com" ? "group" : "overview");
    setError("");
  }

  function handleLogout() { setUser(null); setSelectedStore(null); setPage("group"); }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <div className="logo">MAX IQ</div>
          <h1 style={{margin:"0 0 8px",fontSize:18}}>MAX IQ Dealer Intelligence Platform</h1>
          <label className="field">Email<input className="input" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
          <label className="field">Password<input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
          {error && <div className="error">{error}</div>}
          <button className="btn" onClick={handleLogin}>Login</button>
          <p className="small" style={{margin:0}}>demo@maxqdemo.com / MaxQDemo2026</p>
        </div>
      </div>
    );
  }

  const allDealerItems = dealerNavGroups.flatMap(g => g.items);
  const currentLabel = portal === "admin"
    ? adminNav.find(n => n[0] === page)?.[1] || "Max Q"
    : allDealerItems.find(n => n[0] === page)?.[1] || "Max Q";

  function renderPage() {
    const store = selectedStore || { id:1, name:"Riverside Ford" };
    if (portal === "admin") {
      const pages = {
        "admin-overview": <Admin.AdminOverview />,
        alerts: <Admin.SystemAlerts />,
        dealers: <Admin.AllDealerships onOpenDealer={(d) => { setPortal("dealer"); setSelectedStore(d); setPage("overview"); }} />,
        onboard: <Admin.OnboardDealer />,
        users: <Admin.UserManagement />,
        connections: <Admin.APIConnections />,
        scorecard: <DESScorecard storeId="admin-demo" />,
        activity: <Admin.ActivityLog />,
        billing: <Admin.Billing />,
        settings: <Admin.PlatformSettings />,
        remediation: <RemediationToolkit />,
        whatif: <WhatIfCalculator />,
      };
      return pages[page] || <Admin.AdminOverview />;
    }
    const pages = {
      group: <GroupDashboard onSelectStore={(s) => { setSelectedStore(s); setPage("overview"); }} />,
      overview: <Dealer.SalesDashboard />,
      scorecard: <DESScorecard storeId={store.id} />,
      "sales-dashboard": <Dealer.SalesDashboard />,
      pipeline: <Dealer.SalesPipeline />,
      leads: <Dealer.LeadManagement />,
      marketplace: <Dealer.Marketplace />,
      staff: <Dealer.StaffManagement />,
      mastermind: <Dealer.Mastermind />,
      "service-dashboard": <Dealer.ServiceDashboard />,
      ro: <Dealer.RepairOrders />,
      tech: <Dealer.Technicians />,
      appts: <Dealer.Appointments />,
      csi: <Dealer.CSIScores />,
      parts: <Dealer.Parts />,
      financials: <Dealer.FinancialOverview />,
      reports: <Dealer.Reports />,
      remediation: <RemediationToolkit />,
      whatif: <WhatIfCalculator />,
    };
    const component = pages[page] || <Dealer.SalesDashboard />;
    if (selectedStore && page !== "group") {
      return <StoreView store={selectedStore} onBack={() => { setSelectedStore(null); setPage("group"); }} pageComponent={component} />;
    }
    return component;
  }

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">MAX IQ</div>
        <button className="btn-secondary" style={{fontSize:11,padding:"6px 10px",marginBottom:8}} onClick={() => { setPortal(portal==="dealer"?"admin":"dealer"); setPage(portal==="dealer"?"admin-overview":"group"); setSelectedStore(null); }}>
          Switch to {portal==="dealer"?"Admin":"Dealer"}
        </button>
        {portal === "dealer" ? (
          <GroupedNav groups={dealerNavGroups} page={page} setPage={setPage} />
        ) : (
          adminNav.map(([id,label,Icon]) => (
            <button key={id} className={`nav-btn ${page===id?"active":""}`} onClick={() => setPage(id)}>
              <Icon size={14}/><span>{label}</span>
            </button>
          ))
        )}
        <div className="user-box">
          <strong>{portal==="admin"?"Admin":"Dealer"}</strong><br />{user.email}
        </div>
      </aside>
      <main className="main">
        <header className="topbar">
          <strong>{currentLabel}</strong>
          <button className="btn-secondary" onClick={handleLogout}>Logout</button>
        </header>
        <div className="content">{renderPage()}</div>
      </main>
    </div>
  );
}
